import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Amplify, Auth } from "aws-amplify";
import awsconfig from "../../aws-exports";
import { ConciergeChat } from "../../pages/Chatbox/components/ConciergeChat";
import { observer } from "mobx-react";
import { useStore } from "../../hooks";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Loading } from "../loading";
// Icon ahora se importa de @ann/web-components
import { useScrollToTop } from "../../contexts/ScrollToTopContext";
import { WebSocketProvider } from "../../contexts/WebSocketContext";
import { useWebSocketScope } from "../../hooks";
import { WebSocketScope } from "../../utils/enums";
import { Alert } from "@ann/web-components";
import {
  Wrapper,
  Content,
  Header,
  MenuWrapper,
  TitlePage,
  FloatingChatWrapper,
  SettingsUl,
  TopMenu,
  TopMenuAccountInfo,
  CustomAccountModalBodyListContainerIcon,
  TopNotification,
} from "./App.styled";
import { menuItems, settingsItems } from "./consts";
import ModalSubMenu from "./ModalSubMenu";
import CustomAccountModal from './CustomerAccountModal';
import { hideHelpContainer, adjustWidgetPosition, showHelpButton } from '../../elements/SupporButton/supportUtils';
import Popup from "../../elements/v2/Popup";
import Notifications from "../../elements/v2/Notifications";
import Spinner from "../common/Spinner";
import AgentsWidget  from "./AgentsWidget/AgentsWidget";
import { useUserCheckPermissions } from "../../hooks/useUserCheckPermissions";
import SwitchWorkspaceModal from "../../elements/v2/SwitchWorkspaceModal";
import { Typography, Avatar, Sidebar, Icon } from "@ann/web-components";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { getAvatarUrl, generateAvatarColor } from "../../utils/avatarUtils";
Amplify.configure(awsconfig);

const theme = createTheme({
  palette: {
    primary: { main: "#3D3D3D" },
    secondary: { main: "#FF5722" },
  },
  typography: {
    fontFamily: 'Inter',
  },
});

const App = observer(() => {
	const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
	const [isConciergeCollapsed, setIsConciergeCollapsed] = useState(true);
	const { authStore, title, notificationStore, profileStore, workspaceStore } = useStore();
	const navigate = useNavigate();
	const location = useLocation();
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
	const [isCustomAccountModalOpen, setIsCustomAccountModalOpen] = useState(false);
	const [activeMenuItem, setActiveMenuItem] = useState(null);
	const [customAccountModalItems, setCustomAccountModalItems] = useState([]);
	const [userModalOpen, setUserModalOpen] = useState(false);
	const [observerHelpButton, setObserver] = useState(null);
	const [fullName, setFullName] = useState("");
	const [accountImageFailed, setAccountImageFailed] = useState(false);
	const selectedAccountIdRef = useRef(null);
	const { checkPermission } = useUserCheckPermissions();
	
	// Estado para el Alert de TOAST
	const [toastAlert, setToastAlert] = useState({
		show: false,
		title: "",
		description: "",
		severity: "info"
	});
	
	// ✅ Get scroll-to-top context
	const { containerRef, handleScroll } = useScrollToTop();

	// Detectar si estamos en rutas de workspace
	const isWorkspaceRoute = useMemo(
		() => location?.pathname?.startsWith("/v2/workspace"),
		[location?.pathname]
	);

	// Escuchar eventos TOAST del scope NOTIFICATION
	useWebSocketScope(
		WebSocketScope.NOTIFICATION,
		(message) => {
			const payload = message.payload || {};
			const data = payload.data || payload;
			
			setToastAlert({
				show: true,
				title: payload.title || data.title || "Notification",
				description: payload.description || data.description || payload.message || data.message || "You have a new notification",
				severity: payload.severity || data.severity || "info"
			});
		},
		"TOAST"
	);

	const handleCloseToast = () => {
		setToastAlert(prev => ({ ...prev, show: false }));
	};

	useEffect(() => {
		if (authStore.userData) {
			const {first_name, last_name} = authStore.userData;
			const fullName = `${first_name} ${last_name}`
			setFullName(fullName);
			
		}
		getNotifications();

	}, [authStore.userData])

	useEffect(() => {
		adjustWidgetPosition();
	}, []);

	// Cargar workspaces al inicio cuando tenemos el token (currentWorkspaceUuid)
	// Esto asegura que el workspace del token se seleccione automáticamente
	useEffect(() => {
		if (authStore.currentWorkspaceUuid && 
			!workspaceStore.isLoading &&
			!authStore.isLoading) {
			
			// Verificar si el currentWorkspace coincide con el token
			const currentWorkspaceUuid = workspaceStore.currentWorkspaceUuid;
			const tokenWorkspaceUuid = authStore.currentWorkspaceUuid;
			const needsSync = !currentWorkspaceUuid || 
			                 String(currentWorkspaceUuid) !== String(tokenWorkspaceUuid);
			
			// Si la lista está vacía O el workspace no coincide con el token, recargar
			if (workspaceStore.workspaceList.length === 0 || needsSync) {
				if (needsSync) {
					console.log('🔄 Workspace no coincide con token, recargando lista:', {
						current: currentWorkspaceUuid,
						token: tokenWorkspaceUuid
					});
				} else {
					console.log('🔄 Cargando lista de workspaces inicial desde token:', tokenWorkspaceUuid);
				}
				workspaceStore.resetPagination();
				workspaceStore.fetchPage(null, authStore);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authStore.currentWorkspaceUuid, authStore.isLoading]);

	useEffect(() => {
		// Only load tenants/workspace when in /v2/workspace route (si no se cargó antes)
		if (location?.pathname?.startsWith("/v2/workspace") && 
			workspaceStore.workspaceList.length === 0 && 
			!workspaceStore.isLoading) {
			workspaceStore.resetPagination();
			workspaceStore.fetchPage(null, authStore);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location?.pathname]);

	useEffect(() => {
		selectedAccountIdRef.current = authStore.selectedAccount?.id;
	}, [authStore.selectedAccount])

	const requestToGetNotifications = async (user_id, limit, id) => {
		await notificationStore.getNotificationsRequest({
			customer_id: selectedAccountIdRef.current, user_id, limit, id: null
		});
	}

	useEffect(() => {
		if (authStore.userId && authStore.customerId && !profileStore.displayPhotoURL) {
			profileStore.fetchUserData(authStore.userId, authStore.customerId);
		}
	}, [authStore.userId, authStore.customerId, profileStore.displayPhotoURL]);

	// ✅ Setup scroll-to-top functionality with Context
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		
		const onScroll = () => handleScroll(0.2);
		container.addEventListener('scroll', onScroll, { passive: true });
		
		// Initial check
		handleScroll(0.2);
		
		return () => container.removeEventListener('scroll', onScroll);
	}, [containerRef, handleScroll]);

	const getNotificationInternalRequest = async (user_id, limit) => {
		await authStore.fetchAllUsers()
		await requestToGetNotifications(user_id, limit, null);
	}

	const getNotifications = async () => {
		if (!selectedAccountIdRef.current) return false;
		const user_id = authStore.userData?.id;
		if (!user_id) return false;
		const limit = 10;
		await getNotificationInternalRequest(user_id, limit);

		const notificationInterval = setInterval(async () => {
			await requestToGetNotifications(user_id, limit, null)
		}, 300000);

		return () => {
			clearInterval(notificationInterval);
		};	
	}

	useEffect(() => {
		const mutationObserver = new MutationObserver(() => {
		  hideHelpContainer();
		});
	
		setObserver(mutationObserver);
	
		return () => mutationObserver.disconnect();
	  }, []);

	  const observeIframe = useCallback(() => {
		const iframe = document.querySelector('iframe#jsd-widget');
		if (iframe) {
		  const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
		  if (observerHelpButton) {
			observerHelpButton.observe(iframeDocument, { childList: true, subtree: true });
		  }
		}
	  }, [observerHelpButton]);

	  useEffect(() => {
		const iframe = document.querySelector('iframe#jsd-widget');
		if (iframe) {
		  hideHelpContainer();
		  observeIframe();
		}
	  }, [observeIframe]);
	
	
	useEffect(() => {
		if (authStore.isLoading) {
			authStore.getGlobalInformation();
		}
	}, [authStore]);
	
	useEffect(() => {
		setIsSettingsOpen(false);
	}, [isMenuCollapsed]);
	
	const toggleMenu = (expanded) => {
		if (expanded !== undefined) {
			setIsMenuCollapsed(!expanded);
		} else {
			setIsMenuCollapsed(prev => !prev);
		}
	};
	
	const handleSignOut = useCallback(() => {
		Auth.signOut()
			.then((data) => {
				authStore.clear();
				workspaceStore.clear();  // Limpiar también WorkspaceStore
			})
			.catch((error) => console.log("error signing out: ", error));
	}, [authStore, workspaceStore]);
	
	const getAccountSelector = () => {
		if (!authStore.sortedAccountListByFavs || !Array.isArray(authStore.sortedAccountListByFavs)) {
			return [];
		}
		return authStore.sortedAccountListByFavs.map((item) => ({
			text: item.name,
			id: item.id,
			onClick: () => handleAccountSelector(item.id)
		}));
	}
	
	const handleAccountSelector = useCallback(
		(id) => {
			authStore.pickAccount(id);
		},
		[authStore]
	);

	const memoizedMenuItems = useMemo(() => {
		return menuItems(navigate).filter(item => authStore.hasPermission(item.permissionCode))
			.map(item => ({
				...item, 
				customItems: item.customItems.filter(subitem => authStore.hasPermission(subitem.permissionCode)),
			}));
	}, [navigate, authStore.isGroupSuperAdmin, authStore.isGroupAdmin, authStore.userPermissions.length]);

	// Detectar el item activo basado en la ruta actual
	useEffect(() => {
		const currentPath = location.pathname;
		
		for (const item of memoizedMenuItems) {
			// Verificar si el item tiene link directo y coincide
			if (item.link) {
				const normalizedLink = item.link.replace(/\/$/, '');
				const normalizedPath = currentPath.replace(/\/$/, '');
				if (normalizedPath === normalizedLink || normalizedPath.startsWith(normalizedLink + '/')) {
					setActiveMenuItem(item.text);
					return;
				}
			}
			
			// Verificar si algún subitem coincide
			if (item.customItems && item.customItems.length > 0) {
				for (const subItem of item.customItems) {
					if (subItem.link) {
						const normalizedSubLink = subItem.link.replace(/\/$/, '');
						const normalizedPath = currentPath.replace(/\/$/, '');
						if (normalizedPath === normalizedSubLink || normalizedPath.startsWith(normalizedSubLink + '/')) {
							setActiveMenuItem(item.text);
							return;
						}
					}
				}
			}
		}
	}, [location.pathname, memoizedMenuItems]);

	const handleProfile = () => {
		navigate('/v2/profile')
	}

	const memoizedSettingsItems = useMemo(() => {
		return settingsItems(navigate, handleSignOut, handleProfile);
	}, [navigate, handleSignOut]);

	
	const handleCustomAccountModal = (items) => {
		if(authStore.isGroupSuperAdmin){
			setCustomAccountModalItems(items);
			setIsCustomAccountModalOpen(true);
		}
	}
	
	const closeAccountModal = async () => {
		setIsCustomAccountModalOpen(false);
		const user_id = authStore.userData.id;
		const limit = 10;
		await getNotificationInternalRequest(user_id, limit);
	};
	
	const handleUserModalOpen = (event) => {
		const rect = event.currentTarget.getBoundingClientRect();
		setModalPosition({ top: rect.bottom, left: rect.left });
		setUserModalOpen(true);
	};
	
	const handleUserModalClose = () => {
		setUserModalOpen(false);
	};
	
	const avatarCustomItems = useMemo(() => {
		const avatarItem = memoizedSettingsItems.find(item => item.text === "User");
		return avatarItem ? avatarItem.customItems : [];
	}, [memoizedSettingsItems]);
	
	return authStore.isLoading ? (
		<Loading />
	) : (
		<WebSocketProvider>
			<Wrapper isCollapsed={isMenuCollapsed} title={title} >
			<TopMenu>
				<TopMenuAccountInfo>
					{/* NEW: Avatar with avatarUtils logic - Always use workspace (unified) */}
					{(workspaceStore.currentWorkspace ) &&
					
					
						(() => {
							console.log('workspaceStore.currentWorkspace #####', workspaceStore.currentWorkspace)
							// Prefer workspace, fallback to selectedAccount if workspace not loaded yet
							const workspace = workspaceStore.currentWorkspace;
							const workspaceName = workspace?.tenant_name || 
							                      workspace?.name || 
							                      workspace?.workspaceName || 
							                      authStore.selectedAccount?.name || "";
							const photoUrl = workspace?.photo_url || 
							              workspace?.photoUrl ||
							              workspace?.profilePhotoUrl ||
							              authStore.selectedAccount?.photo_url ||
							              authStore.selectedAccount?.profilePhotoUrl;
							const avatarUrl = accountImageFailed
								? null
								: getAvatarUrl(photoUrl, workspaceName);
							const avatarColor = generateAvatarColor(workspaceName);
							const showInitialsAvatar = accountImageFailed || !avatarUrl;

							const handleAvatarClick = () => {
								if (authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION")) {
									handleCustomAccountModal(getAccountSelector());
								}
							};

							return (
								<div
									onClick={handleAvatarClick}
									style={{
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										backgroundColor: "transparent",
									}}
									data-testid="account-selector-icon"
								>
									{showInitialsAvatar ? (
										<NameInitialsAvatar
											name={workspaceName}
											bgColor={avatarColor}
											textColor="#ffffff"
											size="md"
											borderRadius="50%"
											textWeight="bold"
											textSize="14px"
											borderWidth="0"
										/>
									) : (
										<Avatar
											alt={workspaceName}
											size="md"
											src={avatarUrl}
											onError={() => setAccountImageFailed(true)}
										/>
									)}
								</div>
							);
						})()
					}
					
					{/* OLD: Legacy code using selectedAccount with route distinction - Commented out */}
					{/* 
					{isWorkspaceRoute ? (
						authStore.selectedAccount &&
						(() => {
							const accountName = authStore.selectedAccount.name || "";
							const accountId = authStore.selectedAccount.id || "";
							const photoUrl =
								authStore.selectedAccount.photo_url ||
								authStore.selectedAccount.profilePhotoUrl;
							const avatarUrl = accountImageFailed
								? null
								: getAvatarUrl(photoUrl, accountName);
							const avatarColor = generateAvatarColor(accountName);
							const showInitialsAvatar = accountImageFailed || !avatarUrl;

							const handleAvatarClick = () => {
								if (authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION")) {
									handleCustomAccountModal(getAccountSelector());
								}
							};

							return (
								<div
									onClick={handleAvatarClick}
									style={{
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										backgroundColor: "transparent",
									}}
									data-testid="account-selector-icon"
								>
									{showInitialsAvatar ? (
										<NameInitialsAvatar
											name={accountName}
											bgColor={avatarColor}
											textColor="#ffffff"
											size="md"
											borderRadius="50%"
											textWeight="bold"
											textSize="14px"
											borderWidth="0"
										/>
									) : (
										<Avatar
											alt={accountName}
											size="md"
											src={avatarUrl}
											onError={() => setAccountImageFailed(true)}
										/>
									)}
								</div>
							);
						})()
					) : (
						authStore.selectedAccount && (
						<CustomAccountModalBodyListContainerIcon
							data-testid="account-selector-icon"
							isSuperAdmin={authStore.isGroupSuperAdmin}
							onClick={() =>
								authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION") &&
								handleCustomAccountModal(getAccountSelector())
							}
						>
							{`${authStore.selectedAccount.name
								.split(" ")
								.slice(0, 2)
								.map((word) => word[0])
								.join("")}`}
						</CustomAccountModalBodyListContainerIcon>
						)
					)}
					*/}
					
					{/* NEW: Nombre del workspace actual (unified, always use workspace) */}
					<Typography category="subtitle" size="small" weight="bold">
						{workspaceStore.currentWorkspace?.tenant_name ||
						 workspaceStore.currentWorkspace?.name ||
						 workspaceStore.currentWorkspace?.workspaceName ||
						 authStore.selectedAccount?.name ||
						 ""}
					</Typography>
					{/* {isWorkspaceRoute ? (
						// NEW: Nombre del workspace actual (fallback a la cuenta si aún no hay workspace)
						<Typography category="subtitle" size="small" weight="bold">
							{workspaceStore.currentWorkspace?.name ||
								workspaceStore.currentWorkspace?.tenant_name ||
								authStore.selectedAccount?.name ||
								""}
						</Typography>
					) : (
						// OLD: Nombre de la cuenta seleccionada
						authStore.selectedAccount && (
						<Typography category="subtitle" size="small" weight="bold">
							{authStore.selectedAccount.name}
						</Typography>
						)
					)} */}
				</TopMenuAccountInfo>
				<TopNotification>
					<SettingsUl>
						{/* Chat - Primero */}
						{memoizedSettingsItems
							.filter(item => item.text === 'Chat')
							.map((item, index) => (
								(item.superAdminOnly && authStore.isGroupSuperAdmin) || !item.superAdminOnly ? (
									<div 
										key={`chat-${index}`}
										onClick={() => setIsConciergeCollapsed(!isConciergeCollapsed)}
										style={{ cursor: 'pointer' }}
									>
										<Icon
											color="neutral-white"
											name="aiChat"
										/>
									</div>
								) : null
							))}
						
						{/* Notificaciones - Segundo */}
						{authStore.hasPermission('NOTIFICATIONS_VIEW') && (
							<Popup
								header={<Notifications.Bell data-testid="notifications-icon" />}
								content={<Notifications />}
							/>
						)}
						
						{/* Support - Tercero */}
						{memoizedSettingsItems
							.filter(item => item.text === 'Support')
							.map((item, index) => (
								(item.superAdminOnly && authStore.isGroupSuperAdmin) || !item.superAdminOnly ? (
									<div 
										key={`support-${index}`}
										onClick={showHelpButton}
										style={{ cursor: 'pointer' }}
									>
										<Icon
											color="neutral-white"
											name="contact_support"
										/>
									</div>
								) : null
							))}
						
						{/* User - Cuarto/Último */}
						{memoizedSettingsItems
							.filter(item => item.text === 'User')
							.map((item, index) => {
								const userFullName = fullName || `${authStore.userData?.first_name || ''} ${authStore.userData?.last_name || ''}`.trim();
								const avatarUrl = userFullName 
									? `https://ui-avatars.com/api/?name=${encodeURIComponent(userFullName)}`
									: null;
								
								return (item.superAdminOnly && authStore.isGroupSuperAdmin) || !item.superAdminOnly ? (
									<div 
										key={`user-${index}`}
										onClick={handleUserModalOpen}
										onMouseEnter={handleUserModalOpen}
										style={{ cursor: 'pointer' }}
									>
										{profileStore.isUpdatingProfile ? (
											<Spinner size={20}/>
										) : avatarUrl ? (
											<Avatar
												size="md"
												src={avatarUrl}
												alt={userFullName}
											/>
										) : (
											<Icon
												color="neutral-white"
												name="avatar"
											/>
										)}
									</div>
								) : null;
							})}

					</SettingsUl>
				</TopNotification>
			</TopMenu>

			<MenuWrapper isCollapsed={isMenuCollapsed} id="sidebar-menu-wrapper">
				<Sidebar 
					defaultExpanded={false}
					className="sidebar-menu" 
					position="relative"
					menuTopOffset={40}
					onToggle={toggleMenu}
				>
					{memoizedMenuItems.map((item, index) => {
						const itemKey = item.text || index;
						
						if (item.customItems && item.customItems.length > 0) {
							return (
								<Sidebar.Item
									key={itemKey}
									icon={item.icon}
									text={item.text}
									tooltip={isMenuCollapsed}
									isActive={activeMenuItem === item.text}
									data-testid={`menu-item-${item.text?.toLowerCase()}`}
								>
									<Sidebar.Menu>
										{item.customItems.map((subItem, subIndex) => (
												<Sidebar.Menu.Item
													key={subItem.text || subIndex}
													title={subItem.text}
													icon={subItem.icon}
													onClick={() => {
													setActiveMenuItem(item.text);
														if (subItem.onClick) {
															subItem.onClick();
														}
													}}
												/>
										))}
									</Sidebar.Menu>
								</Sidebar.Item>
							);
						} else {
							return (
								<Sidebar.Item
									key={itemKey}
									icon={item.icon}
									text={item.text}
									tooltip={isMenuCollapsed}
									isActive={activeMenuItem === item.text}
									onClick={() => {
										setActiveMenuItem(item.text);
										if (item.link) {
											navigate(item.link);
										}
									}}
									data-testid={`menu-item-${item.text?.toLowerCase()}`}
								/>
							);
						}
					})}
				</Sidebar>
			</MenuWrapper>

        {/* {title && (
				<Header showDivision={title}>
					<TitlePage>{title}</TitlePage>
				</Header>
			)} */}

			<Content id="main-content" ref={containerRef}>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<Outlet />
				</ThemeProvider>
			</Content>

			<FloatingChatWrapper isCollapsed={isConciergeCollapsed}>
				<AgentsWidget isCollapsed={isConciergeCollapsed} onClose={() => setIsConciergeCollapsed(true)}/>
			</FloatingChatWrapper>


			{/* SWITCH WORKSPACE MODAL - Show in all routes (global, preferred) */}
			{isCustomAccountModalOpen && (
				<SwitchWorkspaceModal
					isOpen={isCustomAccountModalOpen}
					onClose={closeAccountModal}
					onWorkspaceSelect={(workspace) => {
						console.log("Workspace seleccionado:", workspace);
						closeAccountModal();
					}}
				/>
			)}

			{/* CUSTOM ACCOUNT MODAL - Legacy fallback (only if SwitchWorkspaceModal is not available) */}
			{/* Note: This is kept for backward compatibility but SwitchWorkspaceModal is preferred */}

			{userModalOpen && (
				<ModalSubMenu
					position={modalPosition}
					title={fullName}
					subtitle={authStore.userData.email}
					items={avatarCustomItems}
					onClose={handleUserModalClose}
					avatar={true}
				/>
			)}

			{toastAlert.show && (
				<Alert
					title={toastAlert.title}
					description={toastAlert.description}
					severity={toastAlert.severity}
					maxWidth="500px"
					closable={true}
					autoClose={true}
					autoCloseDelay={5000}
					onClose={handleCloseToast}
				/>
			)}
		</Wrapper>
		</WebSocketProvider>
	);
});

export default App;

